import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Accordion, useModal } from '@europrocurement/flexy-components';
import { useTheme, Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import {
    DossierActionContext,
    REFRESH_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierRoutingContext,
    useDossierValidationHeader,
} from '@europrocurement/l2d-modules';
import { enqueueSnackbar } from 'notistack';
import DossierFormaliteProcessesStatus from '../DossierFormaliteProcessesStatus/DossierFormaliteProcessesStatus';
import { useDossierProcesses } from '../../hooks';
import DossierFormaliteProcessesFooter from '../DossierFormaliteProcessesFooter/DossierFormaliteProcessesFooter';
import DossierFormaliteProcessesHeader from '../DossierFormaliteProcessesHeader/DossierFormaliteProcessesHeader';
import DossierFormaliteProcessesList from '../DossierFormaliteProcessesList/DossierFormaliteProcessesList';
import NavigateToExternalUrlModal from '../NavigateToExternalUrlModal/NavigateToExternalUrlModal';

const DossierFormaliteProcesses: React.FunctionComponent = function () {
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();

    const refreshDossier = useCallback(() => {
        if (dispatch) {
            dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
        }
    }, [dispatch]);

    const [isHovered, setIsHovered] = useState<boolean>(false);
    const { palette } = useTheme();
    const [publishProcessing, setPublishProcessing] = useState<boolean>(false);
    const { setHeaderButton, setHeaderTitle, setPathOnExit } = useDossierValidationHeader();
    const navigate = useNavigate();
    const location = useLocation();
    const { modalActions } = useModal();
    const { triggerAction } = useContext(DossierActionContext);
    const {
        isReadyForPublication,
        isFormalityComplete,
        publishGreffeValidatorProcess,
        getFormalitiesStatusLabel,
        getFormalitiesStatusColor,
        canAddFormality,
        getExternalUrlByFormality,
        canPublish,
    } = useDossierProcesses();
    const { getAddFormalityRoute, getDossierValidationRoute } = useDossierRoutingContext();
    const publishable = useMemo(
        () => (dossier ? canPublish(dossier) : false),
        [canPublish, dossier],
    );

    const navigateToInfoValidation = () => {
        const currentLocation = location.pathname + location.search;

        setHeaderTitle(`Vérifier les informations du dossier ${dossier?.numero}`);
        setHeaderButton('Enregister et quitter');
        setPathOnExit(currentLocation);

        const path = getDossierValidationRoute(dossier?.id as number);
        if (path) {
            navigate(path);
        }
    };

    const navigateToParcours = () => {
        const path = getAddFormalityRoute(dossier?.id as number);

        if (path) {
            navigate(path);
        }
    };

    const navigateToExternalValidation = useCallback((externalUrl: string) => {
        window.open(externalUrl, '_blank');
    }, []);

    const handlePublish = async () => {
        if (!dossier) {
            return;
        }
        if (!publishable) {
            enqueueSnackbar(
                'Ce dossier ne peut pas être publié. Veuillez vérifier si toutes les informations sont remplies. (formaliste, SIREN...etc)',
                {
                    variant: 'error',
                },
            );

            return;
        }

        setPublishProcessing(true);

        try {
            const response = await publishGreffeValidatorProcess(dossier);

            if (response.data?.externalUrl) {
                modalActions.call(
                    <NavigateToExternalUrlModal
                        externalUrl={response.data?.externalUrl as string}
                    />,
                );
            }

            enqueueSnackbar('Votre dossier a bien été publié');
        } catch (err) {
            enqueueSnackbar('Une erreur est survenue lors de la publication de votre dossier', {
                variant: 'error',
            });
            console.error(err);
        } finally {
            setPublishProcessing(true);
            refreshDossier();
        }
    };

    const statusLabel = useMemo(
        () => (dossier ? getFormalitiesStatusLabel(dossier) : ''),
        [getFormalitiesStatusLabel, dossier],
    );

    const expandLabel = useMemo(
        () =>
            statusLabel && dossier?.formalites.length ? (
                <DossierFormaliteProcessesStatus label={statusLabel} />
            ) : null,
        [statusLabel, dossier?.formalites.length],
    );
    const formalities = useMemo(
        () =>
            dossier?.formalites.map((formalite) => ({
                libelle: formalite.libelle,
                documentsCount: formalite.documentsCount,
                optionsCount: formalite.optionsCount,
                complete: isFormalityComplete(dossier, formalite),
                externalUrl: getExternalUrlByFormality(dossier, formalite),
                onExternalUrlClick: navigateToExternalValidation,
                id: formalite.id,
            })),
        [dossier, isFormalityComplete, getExternalUrlByFormality, navigateToExternalValidation],
    );

    if (!dossier) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    }
    return (
        <Box
            onMouseOver={() => setIsHovered(true)}
            onMouseOut={() => setIsHovered(false)}
        >
            <Accordion
                title={
                    <DossierFormaliteProcessesHeader
                        organizationLabel={dossier.greffeLibelle as string}
                        onUpdateOrganization={() => triggerAction('selectGreffe', [dossier])}
                    />
                }
                expandLabel={expandLabel}
                expandColor={getFormalitiesStatusColor(dossier)}
                expanded
                sx={{ backgroundColor: palette.background.paper }}
            >
                {formalities && (
                    <DossierFormaliteProcessesList
                        formalities={formalities}
                        isHovered={formalities.length === 1 ? false : isHovered}
                        publishable={publishable}
                    />
                )}
                <DossierFormaliteProcessesFooter
                    canAddFormality={canAddFormality(dossier)}
                    canPublish={isReadyForPublication(dossier)}
                    publishProcessing={publishProcessing}
                    onNavigateToValidation={navigateToInfoValidation}
                    onPublish={handlePublish}
                    onAddFormality={navigateToParcours}
                />
            </Accordion>
        </Box>
    );
};

export default DossierFormaliteProcesses;
